import { Link } from "@mui/material";
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { formatDisplayDateTime } from "utils/dateFormat";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import DocumentShareLinkActionsMenu, {
  ShareLinkStatusChip,
} from "components/DocumentShareLink/DocumentShareLinkActionsMenu";
import {
  fetchAllDocumentShareLinks,
  fetchDocumentShareLinkSends,
  regenerateAnyDocumentShareLink,
  setAnyDocumentShareLinkRevoked,
} from "services/admissionDocumentEmailApi";

const SendHistoryPanel = ({ tokenId }) => {
  const { data: sends = [], isLoading } = useQuery(
    ["document-share-link-sends", tokenId],
    () => fetchDocumentShareLinkSends(tokenId),
    { enabled: !!tokenId, refetchOnWindowFocus: false }
  );

  if (isLoading) {
    return (
      <Box className="p-4 flex items-center gap-2">
        <CircularProgress size={18} />
        <Typography variant="body2">Loading send history…</Typography>
      </Box>
    );
  }
  if (!sends.length) {
    return (
      <Typography variant="body2" className="p-4 text-app-text-muted">
        No recorded sends
      </Typography>
    );
  }

  return (
    <Box className="p-3 bg-app-surface-alt">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-app-text-muted border-b border-app-border">
            <th className="py-1 pr-3 font-medium">Recipient</th>
            <th className="py-1 pr-3 font-medium">Type</th>
            <th className="py-1 pr-3 font-medium">When</th>
            <th className="py-1 pr-3 font-medium">By</th>
            <th className="py-1 font-medium">Success</th>
          </tr>
        </thead>
        <tbody>
          {sends.map((row) => (
            <tr key={row.id} className="border-b border-app-border last:border-b-0">
              <td className="py-1.5 pr-3 text-app-text">{row.recipientEmail}</td>
              <td className="py-1.5 pr-3">{row.recipientType}</td>
              <td className="py-1.5 pr-3">{formatDisplayDateTime(row.sentDate)}</td>
              <td className="py-1.5 pr-3">{row.sentByName || "—"}</td>
              <td className="py-1.5">
                <Chip
                  size="small"
                  label={row.success ? "Yes" : "No"}
                  color={row.success ? "success" : "error"}
                  variant="outlined"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "REVOKED", label: "Revoked" },
  { value: "DISABLED", label: "Disabled" },
  { value: "SUPERSEDED", label: "Superseded" },
];

const DocumentLinks = () => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [busyId, setBusyId] = useState(null);

  const queryKey = [
    "document-share-links",
    pagination.pageIndex,
    pagination.pageSize,
    search,
    status,
  ];
  const { data, isFetching, refetch } = useQuery(
    queryKey,
    () =>
      fetchAllDocumentShareLinks({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        search: search || undefined,
        status: status || undefined,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const rows = data?.content ?? [];
  const rowCount = data?.totalItems ?? 0;

  const columns = useMemo(
    () => [
      {
        accessorKey: "documentName",
        header: "Document",
        size: 180,
        Cell: ({ row }) => row.original.documentName || "Document",
      },
      {
        accessorKey: "studentName",
        header: "Client",
        size: 160,
        Cell: ({ row }) =>
          row.original.studentId ? (
            <Link
              component={RouterLink}
              to={`/admin/admission/edit/${row.original.studentId}`}
              underline="hover"
            >
              {row.original.studentName || `Admission #${row.original.studentId}`}
            </Link>
          ) : (
            "—"
          ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 110,
        Cell: ({ row }) => <ShareLinkStatusChip status={row.original.status} />,
      },
      {
        id: "created",
        header: "Created",
        size: 150,
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">
              {formatDisplayDateTime(row.original.createdDate)}
            </Typography>
            {row.original.createdByName && (
              <Typography variant="caption" className="text-app-text-muted">
                by {row.original.createdByName}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        accessorKey: "sendCount",
        header: "Sends",
        size: 70,
      },
      {
        accessorKey: "lastSentDate",
        header: "Last sent",
        size: 140,
        Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      },
      {
        id: "actions",
        header: "",
        size: 56,
        enableSorting: false,
        Cell: ({ row }) => (
          <DocumentShareLinkActionsMenu
            link={row.original}
            busy={busyId}
            onBusyChange={setBusyId}
            onRefresh={refetch}
            setRevoked={(tokenId, revoked) => setAnyDocumentShareLinkRevoked(tokenId, revoked)}
            regenerate={(tokenId, revokeOld) => regenerateAnyDocumentShareLink(tokenId, revokeOld)}
          />
        ),
      },
    ],
    [busyId, refetch]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    manualPagination: true,
    rowCount,
    onPaginationChange: setPagination,
    state: { pagination, isLoading: isFetching },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableTopToolbar: false,
    enableExpanding: true,
    getRowCanExpand: () => true,
    renderDetailPanel: ({ row }) => <SendHistoryPanel tokenId={row.original.id} />,
    muiTablePaperProps: { elevation: 0, className: "border border-app-border rounded-lg" },
  });

  return (
    <div className="px-4 md:px-10 py-6">
      <Typography variant="h5" className="text-app-text font-semibold mb-1">
        Document Links
      </Typography>
      <Typography variant="body2" className="text-app-text-muted mb-4">
        Permanent admission document share links across all clients.
      </Typography>

      <Paper elevation={0} className="border border-app-border rounded-lg p-4 mb-4">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            size="small"
            label="Search"
            placeholder="Document or client name"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            className="min-w-[220px]"
          />
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            className="min-w-[160px]"
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <MaterialReactTable table={table} />
    </div>
  );
};

export default DocumentLinks;
